/**
 * Food Safety save guard (38.12).
 *
 * React state (`submitting`) only updates on the next render, so a second
 * Enter / click in the same tick slips past a `if (submitting) return` check
 * and saves the reading twice. Logs can't be deleted, so the lock must be
 * synchronous — keep one per screen in a ref (see useFoodSafetySaveLock).
 */

export const FOOD_SAFETY_SAVED_REFRESH_FAILED_MESSAGE =
  "Saved — couldn't refresh. Reload the page to see the latest."

const DEFAULT_KEY = '__save__'

export function createFoodSafetySaveLock() {
  const held = new Set()
  const keyOf = key => (key == null ? DEFAULT_KEY : String(key))
  return {
    isHeld(key) {
      return held.has(keyOf(key))
    },
    isAnyHeld() {
      return held.size > 0
    },
    tryAcquire(key) {
      const k = keyOf(key)
      if (held.has(k)) return false
      held.add(k)
      return true
    },
    release(key) {
      held.delete(keyOf(key))
    },
    /**
     * Runs `task` unless a save with this key is already running.
     * Resolves to `undefined` (task never called) when it is.
     */
    async run(key, task) {
      const k = keyOf(key)
      if (held.has(k)) return undefined
      held.add(k)
      try {
        return await task()
      } finally {
        held.delete(k)
      }
    },
  }
}

/**
 * Duplicate-submit key for `POST /deliveries/batch` (Express 38.5) and
 * `POST /logs` (Express 71): 8–100 of
 * `A-Z a-z 0-9 . _ : -`. Keep one per open receipt and resend it on Retry, so
 * a save whose response was lost replays instead of saving twice.
 */
export function createFoodSafetySubmissionKey({
  now = Date.now(),
  random = Math.random,
} = {}) {
  const noise = Array.from({ length: 2 }, () =>
    Math.floor(random() * 36 ** 6)
      .toString(36)
      .padStart(6, '0'),
  ).join('')
  return `fs-${now.toString(36)}-${noise}`
}

const fingerprintFoodSafetyDraft = draft => {
  try {
    return JSON.stringify(draft ?? null)
  } catch (error) {
    return String(Math.random())
  }
}

/**
 * Duplicate-save keys for `POST /logs` (Express 71), one per save slot
 * (e.g. day + check). Retrying the same draft resends the same key, so a save
 * whose response was lost replays instead of saving a second reading; an
 * edited draft or a cleared slot (after a save) gets a new key.
 */
export function createFoodSafetySubmissionKeys({
  createKey = createFoodSafetySubmissionKey,
} = {}) {
  const slots = new Map()
  return {
    keyFor(slot, draft) {
      const fingerprint = fingerprintFoodSafetyDraft(draft)
      const held = slots.get(slot)
      if (held && held.fingerprint === fingerprint) return held.key
      const key = createKey()
      slots.set(slot, { key, fingerprint })
      return key
    },
    clear(slot) {
      slots.delete(slot)
    },
  }
}

/**
 * Refresh after a save that already succeeded. A failed refresh must never
 * read as a failed save (Retry would then save a duplicate).
 *
 * @returns {Promise<boolean>} true when the refresh worked
 */
export async function refreshAfterFoodSafetySave(refresh) {
  if (typeof refresh !== 'function') return true
  try {
    await refresh()
    return true
  } catch (error) {
    console.error(error)
    return false
  }
}
